import '@utils/polyfills';
// Workaround for parcel-typescript: parcel build does not pick up types
// from src/types/**/*.d.ts, while tsc --noEmit sees no problems
import '@customTypes/typesEntrypoint';

import { AbrController } from '@abrController/AbrController';
import { AbrControllerEvents } from '@abrController/AbrController.event';
import { TMaxResolutionLimits } from '@abrController/Estimators/MaxResolutionsEstimator/IMaxResolutionLimits';
import { BufferController } from '@bufferController/BufferController';
import { BufferControllerEvents } from '@bufferController/BufferController.events';
import { IBufferMetrics } from '@bufferController/BufferMetrics/IBufferMetrics';
import { EPlaybackState } from '@bufferController/PlaybackStateController/IPlaybackStateController';
import { setConfig } from '@configProvider/ConfigConsumer';
import { TDeepPartial } from '@customTypes/common';
import { EventBus } from '@eventBus/EventBus';
import { TDoppioEvents } from '@eventBus/IEventBus';
import { Fragment } from '@fragment/Fragment';
import { FragmentParser } from '@fragmentParser/FragmentParser';
import { FragmentsFilter } from '@fragmentsFilter/FragmentsFilter';
import { FragmentsFilterEvents } from '@fragmentsFilter/FragmentsFilter.events';
import { FragmentsQueue } from '@fragmentsQueue/FragmentsQueue';
import { ELogLevel, LogLevelTitlesMap, Logger, TLogger, TLogLevel, LoggerConsoleTransport } from '@logger/Logger';
import { OpenObserveLogger } from '@logger/OpenObserveLogger';
import { IResolution } from '@resolutionsManager/IResolutionsManager';
import { ResolutionsManager } from '@resolutionsManager/ResolutionsManager';
import { ResolutionsManagerEvents } from '@resolutionsManager/ResolutionsManager.events';
import { destroyWorker } from '@streamLoader/net/NetworkLoader';
import { LegacyEvents } from '@utils/LegacyEvents';
import { buildPlaylistUrl } from '@utils/URL/buildURL';
import bind from '@utils/decorators/bind';
import { NetworkError } from '@utils/errors/Errors';

import { CommonPlayerDebugInfo } from './Debugger/CommonPlayerDebugInfo';
import { Debugger } from './Debugger/Debugger';
import { FragmentsBuffer } from './FragmentsBuffer/FragmentsBuffer';
import { FragmentsBufferEvents } from './FragmentsBuffer/FragmentsBuffer.events';
import { LifecycleEventsManager } from './LifecycleEventsManager/LifecycleEventsManager';
import { LifecycleEventsManagerEvents } from './LifecycleEventsManager/LifecycleEventsManager.events';
import { DEFAULT_LOG_LEVELS, IDoppioPlayerConfig, TDoppioPlayerConfigLike } from './config';
import { EEvent, ELifecycleError, ELifecycleEvent } from './types/events';
import { DOPPIO_VERSION } from './version';

export class DoppioPlayer extends EventTarget {
  private _eventBus: EventBus<TDoppioEvents> = new EventBus();

  private _loggerTransport: LoggerConsoleTransport | null = null;
  private _fragmentParser: FragmentParser = new FragmentParser(this._eventBus);
  private _fragmentQueue: FragmentsQueue = new FragmentsQueue(this._eventBus);
  private _buffer: BufferController = new BufferController(this._eventBus);
  private _resolutionManager: ResolutionsManager = new ResolutionsManager(this._eventBus);
  private _abrController: AbrController = new AbrController(this._eventBus);
  private _debugger: Debugger = new Debugger(this._eventBus);
  private _fragmentsFilter: FragmentsFilter = new FragmentsFilter(this._eventBus);
  private _fragmentsBuffer: FragmentsBuffer = new FragmentsBuffer(this._eventBus);

  private _url: string | null = null;
  private _key: string | null = null;
  private _videoElement: HTMLVideoElement | null = null;
  private _isStarted: boolean = false;

  private readonly _onResolutionChangedListener: string;
  private readonly _onResolutionsAvailableListener: string;
  private readonly _onMetricsAvailableListener: string;
  private readonly _onPlaybackStateChangedListener: string;
  private readonly _onLifecycleEventReportedListener: string;
  private readonly _onLifecycleErrorReportedListener: string;
  private readonly _onFragmentsFilterProblemDetectedListener: string;
  private readonly _onResolutionNotFoundListener: string;
  private readonly _commonMetricsCollector: CommonPlayerDebugInfo = new CommonPlayerDebugInfo();
  private readonly _lifecycleEventsManager: LifecycleEventsManager = new LifecycleEventsManager(this._eventBus);
  private readonly _legacyEventsController: LegacyEvents;
  private _onStreamEndedListener: string;

  private readonly _logger: TLogger;

  constructor() {
    super();
    const instanceId: string = (Math.random() + 1).toString(36).substring(7);
    this._eventBus.setAppId(instanceId);
    this._loggerTransport = new LoggerConsoleTransport(this._eventBus, DEFAULT_LOG_LEVELS, [], []);
    this._logger = Logger(this._eventBus, 'Doppio - core');

    this._logger.info('Created');

    this._onResolutionsAvailableListener = this._eventBus.subscribe(
      ResolutionsManagerEvents.resolutionsAvailable,
      this._onResolutionsAvailable,
    );
    this._onMetricsAvailableListener = this._eventBus.subscribe(
      BufferControllerEvents.bufferMetricsAvailable,
      this._onBufferMetricsAvailable,
    );
    this._onResolutionChangedListener = this._eventBus.subscribe(ResolutionsManagerEvents.resolutionChanged, this._onResolutionChanged);
    this._onStreamEndedListener = this._eventBus.subscribe(BufferControllerEvents.endOfStreamReached, this._onStreamEnded);
    this._onPlaybackStateChangedListener = this._eventBus.subscribe(
      BufferControllerEvents.playbackStateChanged,
      this._onPlaybackStateChanged,
    );

    this._legacyEventsController = new LegacyEvents(this.dispatchEvent.bind(this), this._eventBus);

    this._debugger.registerDebuggableModules({
      fragmentQueue: this._fragmentQueue,
      commonInfo: this._commonMetricsCollector,
      resolutionManager: this._resolutionManager,
      abr: this._abrController,
    });

    this._onLifecycleEventReportedListener = this._eventBus.subscribe(
      LifecycleEventsManagerEvents.lifecycleEvent,
      this._onLifecycleEventReported,
    );

    this._onLifecycleErrorReportedListener = this._eventBus.subscribe(
      LifecycleEventsManagerEvents.lifecycleError,
      this._onLifecycleErrorReported,
    );
    this._onResolutionNotFoundListener = this._eventBus.subscribe(ResolutionsManagerEvents.resolutionNotFound, this._onResolutionNotFound);
    this._onFragmentsFilterProblemDetectedListener = this._eventBus.subscribe(
      FragmentsFilterEvents.problemDetected,
      this._onFragmentFilterProblemDetected,
    );
  }

  public destroy(): void {
    this._eventBus.unsubscribe(this._onResolutionsAvailableListener);
    this._eventBus.unsubscribe(this._onResolutionChangedListener);
    this._eventBus.unsubscribe(this._onMetricsAvailableListener);
    this._eventBus.unsubscribe(this._onStreamEndedListener);
    this._eventBus.unsubscribe(this._onPlaybackStateChangedListener);
    this._eventBus.unsubscribe(this._onLifecycleEventReportedListener);
    this._eventBus.unsubscribe(this._onLifecycleErrorReportedListener);
    this._eventBus.unsubscribe(this._onResolutionNotFoundListener);
    this._eventBus.unsubscribe(this._onFragmentsFilterProblemDetectedListener);

    this._fragmentParser.destroy();
    this._buffer.destroy();
    this._resolutionManager.destroy();
    this._abrController.destroy();
    this._fragmentQueue.destroy();
    this._debugger.destroy();
    this._legacyEventsController.destroy();
    this._videoElement?.removeEventListener('error', this._onMediaElementError);
    this._logger.info('Destroyed');
    this._lifecycleEventsManager.destroy();
    this._fragmentsFilter.destroy();
    this._fragmentsBuffer.destroy();

    setTimeout(() => {
      this._loggerTransport?.destroy();
    }, 1000);

    destroyWorker(this._eventBus.getAppId());
  }

  get version(): string {
    return DOPPIO_VERSION;
  }

  get isStarted(): boolean {
    return this._isStarted;
  }

  public recoverMediaError(): void {
    this._buffer.detachVideoElement();

    if (this._videoElement) {
      this._buffer.attachVideoElement(this._videoElement);
    }

    this._resolutionManager.restart();
    this._videoElement?.play();
  }

  public start(): void {
    if (!this._url) {
      this._logger.fatal('Stream URL is not defined. Cannot start player');

      return;
    }

    if (!this._videoElement) {
      this._logger.warn('Video element is not defined.');
    }

    if (this._isStarted) {
      this._restart();
      return;
    }

    this._debugger.registerDebuggableModules({ bufferController: this._buffer });

    this._resolutionManager.setUrl(this._url);

    this._isStarted = true;
  }

  public setUrl(newUrl: string): DoppioPlayer {
    if (!newUrl.trim()) {
      this._stop();
      return this;
    }

    const url: string = buildPlaylistUrl(newUrl);

    const shouldRestart: boolean = this._url !== url && this._isStarted;

    this._url = url;
    this._key = null;

    if (shouldRestart) {
      this._lifecycleEventsManager.reset();
      this._restart();
    }

    //this._addAirPlaySourceIfNeeded();
    return this;
  }

  public setKey(key: string): DoppioPlayer {
    this._key = key;

    return this;
  }

  public setVideoElement(videoElement: HTMLVideoElement | null): DoppioPlayer {
    if (videoElement === this._videoElement) {
      return this;
    }

    if (this._videoElement) {
      this._buffer.detachVideoElement();
      this._videoElement.removeEventListener('error', this._onMediaElementError);
    }

    this._videoElement = videoElement;

    if (this._videoElement) {
      this._buffer.attachVideoElement(this._videoElement);
      this._buffer.scheduleSourceBufferRecreation();

      this._videoElement.addEventListener('error', this._onMediaElementError);
    }

    //this._addAirPlaySourceIfNeeded();
    return this;
  }

  public getFragmentQueue(): Fragment[] {
    return this._fragmentQueue.getCurrentQueue();
  }

  public setLoggerLevel(levels: TLogLevel[], additionalScopes: string[] = [], ignoreDomains: string[] = []): DoppioPlayer {
    if (this._loggerTransport) {
      this._loggerTransport.destroy();
      this._loggerTransport = null;
    }

    this._loggerTransport = new LoggerConsoleTransport(this._eventBus, levels, additionalScopes, ignoreDomains);

    return this;
  }

  public setResolutionByName(name: string): DoppioPlayer {
    this._resolutionManager.setResolutionByName(name);

    return this;
  }

  public setResolutionByBitrate(expectedBitrate: number): void {
    return this._resolutionManager.setNearestResolutionByBitrate(expectedBitrate);
  }

  public disableABR(): DoppioPlayer {
    this._resolutionManager.toggleAutoResolution(false);

    return this;
  }

  public enableABR(): DoppioPlayer {
    this._resolutionManager.toggleAutoResolution(true);

    return this;
  }

  public setConfig(config: TDeepPartial<IDoppioPlayerConfig>): void {
    setConfig(config, this._eventBus);
  }

  public setMaxABRResolution(resolution: TMaxResolutionLimits): void {
    this._eventBus.publish(AbrControllerEvents.resolutionsConstraintsAvailable, resolution);
  }

  private async _restart(): Promise<void> {
    this._logger.info('restarting');
    this._buffer?.reset();
    await this.recoverMediaError();
    this._fragmentParser.destroy();
    this._fragmentParser = new FragmentParser(this._eventBus);
    this._fragmentQueue.reset();

    if (this._url) {
      this._resolutionManager.setUrl(this._url);
    }
  }

  // todo: make public, but be sure to not break mmp logic
  private _stop(): void {
    this._url = null;
    this._restart();
    this._resolutionManager.setUrl('');
  }

  @bind
  private _onResolutionsAvailable(resolutions: IResolution[]): void {
    this.dispatchEvent(new CustomEvent(EEvent.VARIANTS_DETECTED, { detail: resolutions }));
  }

  @bind
  private _onStreamEnded(): void {
    this.dispatchEvent(new Event(EEvent.STREAM_ENDED));
  }

  @bind
  private _onResolutionChanged(resolution: IResolution): void {
    this.dispatchEvent(new CustomEvent(EEvent.RESOLUTION_CHANGED, { detail: resolution }));
  }

  @bind
  private _onBufferMetricsAvailable(metrics: IBufferMetrics): void {
    this.dispatchEvent(new CustomEvent(EEvent.METRICS_AVAILABLE, { detail: metrics }));
  }

  @bind
  private _onPlaybackStateChanged({ prevState, nextState }: { prevState: EPlaybackState; nextState: EPlaybackState }): void {
    this.dispatchEvent(new CustomEvent(EEvent.PLAYBACK_STATE_CHANGED, { detail: { prevState, nextState } }));
  }

  @bind
  private _onFragmentFilterProblemDetected({ reason, fragment }: { reason: string; fragment: string }): void {
    this.dispatchEvent(new CustomEvent(EEvent.FRAGMENT_FILTERED_OUT, { detail: { reason, fragment } }));
  }

  @bind
  private _onMediaElementError(): void {
    OpenObserveLogger.reportError('VideoElementError', { msg: this._videoElement?.error?.message, code: this._videoElement?.error?.code });
    this._eventBus.publish(FragmentsBufferEvents.saveData, {});
    this._logger.error(`Media error occurred ${this._videoElement?.error?.message} ${this._videoElement?.error?.code}`);
    if (this._videoElement?.error) {
      this.recoverMediaError();
    }
  }

  @bind
  private _onLifecycleEventReported({ eventType }: { eventType: ELifecycleEvent }): void {
    this.dispatchEvent(new CustomEvent(EEvent.LIFECYCLE_EVENT, { detail: { eventType } }));
  }

  @bind
  private _onLifecycleErrorReported({ errorType, nativeError }: { errorType: ELifecycleError; nativeError: Error }): void {
    this.dispatchEvent(new CustomEvent(EEvent.LIFECYCLE_ERROR, { detail: { errorType, nativeError } }));
  }

  @bind
  private _onResolutionNotFound(): void {
    this.dispatchEvent(new CustomEvent(EEvent.RESOLUTION_NOT_FOUND));
  }

  private _addAirPlaySourceIfNeeded(): void {
    if (this._url && this._videoElement && window.ManagedMediaSource) {
      const airplaySource: HTMLSourceElement = document.createElement('source');
      airplaySource.type = 'application/x-mpegURL';
      airplaySource.src = this._url.replace(/playlistType=lowLatency/gi, '');
      this._videoElement.appendChild(airplaySource);
    }
  }
}

export type TResolution = IResolution;

export type TBufferMetrics = IBufferMetrics;

export type TDoppioPlayerConfig = IDoppioPlayerConfig;

export type TResolutionsLimits = TMaxResolutionLimits;

export type TDoppioPlayerAvailableConfigValues = TDoppioPlayerConfigLike;

export type TDoppioNetworkError = NetworkError;

export { ELogLevel, EEvent, LogLevelTitlesMap, EPlaybackState, ELifecycleEvent, ELifecycleError };

export { defaultConfig, availableConfigValues } from './config';
export { DOPPIO_VERSION };
